import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { getLivePrice, getPerformance, getPriceTrend, getUserAsset } from '../api';
import {
  InsuranceAssetType,
  InsuranceIndexFilterEnum,
  TLivePrice,
  TPerformance,
  TPriceTrend,
  TUserAsset,
} from '../api/types';
import { ErrorResponse } from '@shared/api';
import { USER_GOLD_ASSET_CACHE_KEY } from '@entities/gold-plan';

export const useUserGoldAsset = (assetType: InsuranceAssetType, options?: { enabled?: boolean }) =>
  useQuery<TUserAsset, ErrorResponse>({
    queryKey: [USER_GOLD_ASSET_CACHE_KEY, assetType],
    queryFn: () => getUserAsset(assetType),
    enabled: options?.enabled ?? true,
    retry: false,
  });

export const useGoldLivePrice = (assetType: InsuranceAssetType, options?: { enabled?: boolean }) =>
  useQuery<TLivePrice>({
    queryKey: ['investment-gold', 'live-price', assetType],
    queryFn: () => getLivePrice(assetType),
    enabled: options?.enabled ?? true,
  });

export const useGoldPerformance = (
  assetType: InsuranceAssetType,
  options?: { enabled?: boolean }
) =>
  useQuery<TPerformance>({
    queryKey: ['investment-gold', 'performance', assetType],
    queryFn: () => getPerformance(assetType),
    enabled: options?.enabled ?? true,
  });

export const useGoldPriceTrend = (
  assetType: InsuranceAssetType,
  filter: InsuranceIndexFilterEnum,
  options?: { enabled?: boolean }
) =>
  useQuery<TPriceTrend>({
    queryKey: ['investment-gold', 'price-trend', assetType, filter],
    queryFn: () => getPriceTrend(assetType, filter),
    enabled: options?.enabled ?? true,
  });

export const useGoldPriceTrendChart = () => {
  const [filter, setFilter] = useState<InsuranceIndexFilterEnum>(InsuranceIndexFilterEnum.Daily);

  const handleFilterChange = useCallback((newFilter: InsuranceIndexFilterEnum) => {
    setFilter(newFilter);
  }, []);

  const query = useGoldPriceTrend(InsuranceAssetType.Gold, filter, {
    enabled: Boolean(InsuranceAssetType.Gold),
  });

  return {
    priceTrend: query.data,
    isLoading: query.isFetching && (!query.data || query.isError),
    isError: query.isError && !query.isFetching,
    refetch: query.refetch,
    activeFilter: filter,
    onFilterChange: handleFilterChange,
  };
};
